// Core
import React, { Component } from "react";

// Router
import { $state } from "../../../../router/";

// Logic
import {
  DATE_FORMAT,
  DATE_RANGE_OPTIONS,
  DEFAULT_DATE_RANGE,
  buildUrlParams,
  detectPreset,
  presetRange,
} from "../../../../Logic/WidgetAnalytics/filters";
import WidgetAnalyticsLogic from "../../../../Logic/WidgetAnalytics";

// Components
import {
  GECKO_CONSTS,
  GeckoBtnsSorted,
  GeckoCardBody,
  GeckoCardHeader,
  GeckoCardWrapper,
  GeckoFields,
} from "@geckolabs/gecko-react-ui";

const CUSTOM_RANGE = "custom";

const RANGE_OPTIONS = DATE_RANGE_OPTIONS.map(({ label, value }) => ({
  label,
  value: String(value),
})).concat({ label: "Custom range", value: CUSTOM_RANGE });

const appliedKey = (filters = {}) =>
  [filters.accountId, filters.widgetId, filters.from, filters.to].join("|");

const draftFromFilters = (filters = {}) => {
  const preset = detectPreset(filters.from, filters.to);

  return {
    accountId: filters.accountId || null,
    widgetId: filters.widgetId || null,
    range: preset === null ? CUSTOM_RANGE : String(preset),
    from: filters.from,
    to: filters.to,
  };
};

export default class WidgetAnalyticsFilters extends Component {
  static getDerivedStateFromProps(nextProps, prevState) {
    const nextKey = appliedKey(nextProps.filters);

    if (nextKey === prevState.appliedKey) {
      return null;
    }

    return {
      appliedKey: nextKey,
      draft: draftFromFilters(nextProps.filters),
      widgets: nextProps.filterOptions.widgets || [],
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      appliedKey: appliedKey(props.filters),
      draft: draftFromFilters(props.filters),
      widgets: props.filterOptions.widgets || [],
    };

    this.handleFieldChange = this.handleFieldChange.bind(this);
    this.apply = this.apply.bind(this);
    this.reset = this.reset.bind(this);
  }

  handleFieldChange(key, value) {
    this.setState(
      (state) => ({
        draft: this.nextDraft(state.draft, key, value),
        widgets: key === "accountId" ? [] : state.widgets,
      }),
      () => {
        if (key === "accountId") {
          this.loadWidgets(value);
        }
      }
    );
  }

  nextDraft(draft, key, value) {
    const next = Object.assign({}, draft, { [key]: value });

    if (key === "range" && value !== CUSTOM_RANGE) {
      return Object.assign(next, presetRange(Number(value)));
    }

    if (key === "accountId") {
      next.widgetId = null;
    }

    return next;
  }

  loadWidgets(accountId) {
    if (!accountId) {
      this.setState({ widgets: [] });
      return;
    }

    WidgetAnalyticsLogic.getWidgets(accountId)
      .then((widgets) => {
        this.setState({ widgets });
      })
      .catch(() => {
        this.setState({ widgets: [] });
      });
  }

  fields() {
    const { draft, widgets } = this.state;
    const { accounts } = this.props.filterOptions;
    const isCustomRange = draft.range === CUSTOM_RANGE;

    return [
      {
        key: "accountId",
        label: "Account",
        type: GECKO_CONSTS.FIELD_TYPE_SELECT,
        width: "33%",
        options: accounts.map((account) => ({
          value: account.id,
          label: account.name,
        })),
        config: { clearable: false, placeholder: "Select an account" },
      },
      {
        key: "widgetId",
        label: "Widget",
        type: GECKO_CONSTS.FIELD_TYPE_SELECT,
        width: "33%",
        options: (widgets || []).map((widget) => ({
          value: widget.id,
          label: widget.name,
        })),
        disabledWhen: () => !draft.accountId,
        config: {
          clearable: true,
          placeholder: draft.accountId
            ? "All widgets"
            : "Select an account first",
        },
      },
      {
        key: "range",
        label: "Time period",
        type: GECKO_CONSTS.FIELD_TYPE_SELECT,
        width: "33%",
        options: RANGE_OPTIONS,
        config: { clearable: false },
      },
      {
        key: "from",
        label: "From",
        type: GECKO_CONSTS.FIELD_TYPE_DATE,
        width: "50%",
        format: DATE_FORMAT,
        display_format: "DD/MM/YYYY",
        hideWhen: () => !isCustomRange,
      },
      {
        key: "to",
        label: "To",
        type: GECKO_CONSTS.FIELD_TYPE_DATE,
        width: "50%",
        format: DATE_FORMAT,
        display_format: "DD/MM/YYYY",
        hideWhen: () => !isCustomRange,
      },
    ];
  }

  goTo(filters) {
    return $state.go(
      "widget-analytics",
      buildUrlParams(Object.assign({}, filters, { page: 1 }))
    );
  }

  apply() {
    if (!this.state.draft.accountId) {
      return;
    }

    return this.goTo(this.state.draft);
  }

  reset() {
    return this.goTo(
      Object.assign(
        { accountId: this.state.draft.accountId, widgetId: null },
        presetRange(DEFAULT_DATE_RANGE)
      )
    );
  }

  btns() {
    return [
      {
        name: "Apply",
        preset: GECKO_CONSTS.BTN_PRESET_SAVE,
        secondary: true,
        handler: this.apply,
      },
      {
        name: "Reset",
        preset: GECKO_CONSTS.BTN_PRESET_CLOSE,
        secondary: true,
        handler: this.reset,
      },
    ];
  }

  render() {
    return (
      <GeckoCardWrapper className="mb-3">
        <GeckoCardHeader name="Filters" icon="filter" />
        <GeckoCardBody padded>
          <GeckoFields
            fields={this.fields()}
            values={this.state.draft}
            handler={this.handleFieldChange}
          />
          <div className="widgetAnalytics-filter-actions">
            <GeckoBtnsSorted items={this.btns()} />
          </div>
        </GeckoCardBody>
      </GeckoCardWrapper>
    );
  }
}
